import { Link, NavLink } from "react-router-dom";
import { EyeSlash } from "@phosphor-icons/react";
import { useCinemaMode } from "@/lib/useCinemaMode";

/**
 * Workspace top bar — SMIN-style clean, light, professional.
 *
 * · Height: 64 px, sticky at top
 * · Background: pure white, single 1 px slate-200 bottom border
 * · Full-width — content is NOT max-w-constrained; it sits flush to the
 *   edges of the main pane so the logo aligns with the sidebar's right
 *   edge and the right-slot aligns with the viewport's right edge.
 * · Left:  compact SRES horizontal lockup + optional page nav
 * · Right: application slot (unit toggle, user menu, etc.)
 * · Blue #1E6FFF is the ONLY accent — used for active nav underline,
 *   focus rings, and active states inside the right slot.
 * · No theme toggle — SRES is strictly light-themed.
 *
 * Iter112 · Feb 2026 · Cinema-mode aware. When the shared cinema flag
 * is on the whole header hides itself so the viewport is reclaimed for
 * 3D viewers / GIS maps / block-model previews. A small eye-slash
 * button just before the right-slot lets the user toggle cinema mode
 * from the header on any device (previously only reachable from the
 * wizard sidebar controls).
 */
export default function BrandHeader({ navLinks = [], rightSlot = null, leftSlot = null, brandTo = "/" }) {
  const [cinema, , toggleCinema] = useCinemaMode();
  if (cinema) return null;
  return (
    <header
      className="sticky top-0 z-30 h-14 md:h-16 bg-white border-b border-slate-200"
      data-testid="brand-header"
    >
      <div className="h-full w-full px-3 sm:px-4 md:px-8 flex items-center gap-2 sm:gap-4 md:gap-6">
        {/* -- Optional left slot (hamburger on mobile) ------------------ */}
        {leftSlot ? <div className="flex items-center shrink-0">{leftSlot}</div> : null}

        {/* -- Left: HD SRES logo lockup --------------------------------- */}
        <Link
          to={brandTo}
          className="flex items-center gap-3 shrink-0 hover:opacity-90 transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue focus-visible:ring-offset-2 rounded-md"
          data-testid="header-brand"
        >
          <img
            src="/sres-logo-horizontal.png?v=5"
            alt="SRES · Shanvi Resource Estimation Studio"
            className="h-8 md:h-10 w-auto object-contain select-none"
            draggable={false}
          />
        </Link>

        {/* -- Middle: optional page nav (empty inside workspace) -------- */}
        {navLinks.length > 0 ? (
          <nav
            className="hidden md:flex items-center gap-8 text-[13px] font-medium text-slate-700"
            data-testid="header-nav"
          >
            {navLinks.map((n) => {
              const base = "hover:text-brand-blue transition-colors py-1 border-b-2 border-transparent";
              if (n.to) {
                return (
                  <NavLink
                    key={n.label}
                    to={n.to}
                    end={n.end}
                    className={({ isActive }) => `${base} ${isActive ? "text-brand-blue border-brand-blue" : ""}`}
                    data-testid={`header-nav-${n.label.toLowerCase()}`}
                  >
                    {n.label}
                  </NavLink>
                );
              }
              return (
                <a
                  key={n.label}
                  href={n.href}
                  className={base}
                  data-testid={`header-nav-${n.label.toLowerCase()}`}
                >
                  {n.label}
                </a>
              );
            })}
          </nav>
        ) : null}

        {/* -- Spacer pushes the right-slot to the far edge -------------- */}
        <div className="flex-1" />

        {/* -- Cinema-mode toggle (Iter112) ------------------------------ */}
        <button
          type="button"
          title="Hide top nav & sidebar (⌘/Ctrl + Shift + \\)"
          aria-label="Enter cinema mode"
          onClick={toggleCinema}
          data-testid="header-cinema-toggle"
          className="hidden sm:inline-flex w-8 h-8 rounded-md border border-slate-200 text-slate-500 hover:text-brand-blue hover:border-brand-blue items-center justify-center transition"
        >
          <EyeSlash size={14} weight="bold" />
        </button>

        {/* -- Right: application slot ---------------------------------- */}
        <div className="flex items-center gap-1.5 sm:gap-2 md:gap-3 shrink-0">
          {rightSlot}
        </div>
      </div>
    </header>
  );
}
